import axios from 'axios';

import { convertToUSDT } from './coindesk';

// Configuración de HotelBeds desde variables de entorno
const HOTELBEDS_KEY = import.meta.env.VITE_HOTELBEDS_KEY;
const HOTELBEDS_SECRET = import.meta.env.VITE_HOTELBEDS_SECRET;
const TRAVELER_COMMISSION = parseFloat(
  import.meta.env.VITE_TRAVELER_COMMISION || '0.10',
);

// En desarrollo, usar el proxy de Vite. En producción, usar la URL directa
const isDevelopment = import.meta.env.DEV;
const baseURL = isDevelopment
  ? '/api/hotelbeds/hotel-content-api/1.0'
  : `https://${import.meta.env.VITE_HOTELBEDS_HOST}/hotel-content-api/1.0`;

const bookingBaseURL = isDevelopment
  ? '/api/hotelbeds/hotel-api/1.0'
  : `https://${import.meta.env.VITE_HOTELBEDS_HOST}/hotel-api/1.0`;

const activityBaseURL = isDevelopment
  ? '/api/hotelbeds/activity-api/3.0'
  : `https://${import.meta.env.VITE_HOTELBEDS_HOST}/activity-api/3.0`;

const activityContentBaseURL = isDevelopment
  ? '/api/hotelbeds/activity-content-api/1.0'
  : `https://${import.meta.env.VITE_HOTELBEDS_HOST}/activity-content-api/1.0`;

// Cliente axios configurado para HotelBeds Content API
const hotelbedsClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Cliente axios configurado para HotelBeds Booking API
const hotelbedsBookingClient = axios.create({
  baseURL: bookingBaseURL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Cliente axios configurado para HotelBeds Activity API
const hotelbedsActivityClient = axios.create({
  baseURL: activityBaseURL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Cliente axios configurado para HotelBeds Activity Content API
const hotelbedsActivityContentClient = axios.create({
  baseURL: activityContentBaseURL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * Genera la firma de autenticación requerida por HotelBeds
 * X-Signature = SHA256(ApiKey + Secret + Timestamp)
 */
const generateSignature = async (): Promise<{
  signature: string;
  timestamp: string;
}> => {
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const concatenated = HOTELBEDS_KEY + HOTELBEDS_SECRET + timestamp;

  // Usar Web Crypto API para generar SHA-256
  const encoder = new TextEncoder();
  const data = encoder.encode(concatenated);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const signature = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return { signature, timestamp };
};

/**
 * Añade headers de autenticación a cada request del Content API
 */
hotelbedsClient.interceptors.request.use(async (config) => {
  const { signature } = await generateSignature();

  config.headers['Api-key'] = HOTELBEDS_KEY;
  config.headers['X-Signature'] = signature;

  return config;
});

/**
 * Añade headers de autenticación a cada request del Booking API
 */
hotelbedsBookingClient.interceptors.request.use(async (config) => {
  const { signature } = await generateSignature();

  config.headers['Api-key'] = HOTELBEDS_KEY;
  config.headers['X-Signature'] = signature;

  return config;
});

/**
 * Añade headers de autenticación a cada request del Activity API
 */
hotelbedsActivityClient.interceptors.request.use(async (config) => {
  const { signature } = await generateSignature();

  config.headers['Api-key'] = HOTELBEDS_KEY;
  config.headers['X-Signature'] = signature;

  return config;
});

/**
 * Añade headers de autenticación a cada request del Activity Content API
 */
hotelbedsActivityContentClient.interceptors.request.use(async (config) => {
  const { signature } = await generateSignature();

  config.headers['Api-key'] = HOTELBEDS_KEY;
  config.headers['X-Signature'] = signature;

  return config;
});

// Interfaces para las respuestas de la API
export interface Destination {
  code: string;
  name: {
    content: string;
  };
  countryCode: string;
}

export interface Zone {
  code: number;
  name: string;
  description?: string;
}

export interface Hotel {
  code: number;
  name: {
    content: string;
  };
  destinationCode: string;
  zoneCode?: number;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  categoryCode?: string;
  categoryName?: string;
}

export interface ContentSearchParams {
  fields?: string;
  language?: string;
  from?: number;
  to?: number;
  countries?: string;
  destinationCodes?: string;
  countryCodes?: string;
  useSecondaryLanguage?: boolean;
}

// Cache para almacenar resultados y evitar llamadas repetidas
let destinationsCache: Destination[] = [];
let hotelsCache: Hotel[] = [];
let totalDestinations = 0;
let totalHotels = 0;
const ITEMS_PER_PAGE = 1000;

/**
 * Busca destinos por nombre con infinite scroll
 * Carga 1000 resultados a la vez y filtra localmente
 * Usa carga paralela para optimizar tiempos
 */
export const searchDestinations = async (
  searchTerm: string,
  params?: ContentSearchParams,
): Promise<Destination[]> => {
  try {
    // Si el cache está vacío, cargar la primera página
    if (destinationsCache.length === 0) {
      const response = await hotelbedsClient.get('/locations/destinations', {
        params: {
          fields: 'code,countryCode,name',
          language: 'ENG',
          from: 1,
          to: ITEMS_PER_PAGE,
          ...params,
        },
      });

      destinationsCache = response.data.destinations || [];
      totalDestinations = response.data.total || 0;
    }

    // Filtrar por término de búsqueda en el cache
    let filteredResults: Destination[] = [];
    if (searchTerm) {
      filteredResults = destinationsCache.filter((dest: Destination) => {
        // Validar que el destino tenga la estructura correcta
        if (!dest.name || !dest.name.content) return false;
        return dest.name.content
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });
      // Seguir cargando más datos mientras haya menos de 10 resultados y no hayamos cargado todo
      while (
        filteredResults.length < 10 &&
        destinationsCache.length < totalDestinations
      ) {
        const itemsLoaded = destinationsCache.length;
        const itemsRemaining = totalDestinations - itemsLoaded;

        // Calcular cuántas peticiones paralelas hacer (máximo 5)
        const parallelRequests = Math.min(
          5,
          Math.ceil(itemsRemaining / ITEMS_PER_PAGE),
        );
        // Crear promesas para las peticiones paralelas
        const promises = [];
        for (let i = 0; i < parallelRequests; i++) {
          const from = itemsLoaded + i * ITEMS_PER_PAGE + 1;
          const to = Math.min(from + ITEMS_PER_PAGE - 1, totalDestinations);

          promises.push(
            hotelbedsClient.get('/locations/destinations', {
              params: {
                fields: 'code,countryCode,name',
                language: 'ENG',
                from,
                to,
                ...params,
              },
            }),
          );
        }

        try {
          // Ejecutar todas las peticiones en paralelo
          const responses = await Promise.all(promises);

          // Combinar todos los resultados
          let allNewDestinations: Destination[] = [];
          responses.forEach((response) => {
            const newDests = response.data.destinations || [];
            allNewDestinations = [...allNewDestinations, ...newDests];
          });

          if (allNewDestinations.length === 0) {
            break;
          }

          destinationsCache = [...destinationsCache, ...allNewDestinations];
          // Volver a filtrar con el cache actualizado
          filteredResults = destinationsCache.filter((dest: Destination) => {
            // Validar que el destino tenga la estructura correcta
            if (!dest.name || !dest.name.content) return false;
            return dest.name.content
              .toLowerCase()
              .includes(searchTerm.toLowerCase());
          });
        } catch (_error) {
          break;
        }
      }
    } else {
      filteredResults = destinationsCache;
    }

    return filteredResults.slice(0, 10);
  } catch (_error) {
    return [];
  }
};

/**
 * Busca hoteles por nombre con infinite scroll
 * Carga 1000 resultados a la vez y filtra localmente
 * Usa carga paralela para optimizar tiempos
 */
export const searchHotels = async (
  searchTerm: string,
  params?: ContentSearchParams,
): Promise<Hotel[]> => {
  try {
    // Si el cache está vacío, cargar la primera página
    if (hotelsCache.length === 0) {
      const response = await hotelbedsClient.get('/hotels', {
        params: {
          fields: 'code,name',
          language: 'ENG',
          from: 1,
          to: ITEMS_PER_PAGE,
          ...params,
        },
      });
      hotelsCache = response.data.hotels || [];
      totalHotels = response.data.total || 0;
    }

    // Filtrar por término de búsqueda en el cache
    let filteredResults: Hotel[] = [];
    if (searchTerm) {
      filteredResults = hotelsCache.filter((hotel: Hotel) => {
        // Validar que el hotel tenga la estructura correcta
        if (!hotel.name || !hotel.name.content) return false;
        return hotel.name.content
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });

      // Seguir cargando más datos mientras haya menos de 10 resultados y no hayamos cargado todo
      while (filteredResults.length < 10 && hotelsCache.length < totalHotels) {
        const itemsLoaded = hotelsCache.length;
        const itemsRemaining = totalHotels - itemsLoaded;

        // Calcular cuántas peticiones paralelas hacer (máximo 5)
        const parallelRequests = Math.min(
          5,
          Math.ceil(itemsRemaining / ITEMS_PER_PAGE),
        );

        // Crear promesas para las peticiones paralelas
        const promises = [];
        for (let i = 0; i < parallelRequests; i++) {
          const from = itemsLoaded + i * ITEMS_PER_PAGE + 1;
          const to = Math.min(from + ITEMS_PER_PAGE - 1, totalHotels);

          promises.push(
            hotelbedsClient.get('/hotels', {
              params: {
                fields: 'code,name',
                language: 'ENG',
                from,
                to,
                ...params,
              },
            }),
          );
        }

        try {
          // Ejecutar todas las peticiones en paralelo
          const responses = await Promise.all(promises);

          // Combinar todos los resultados
          let allNewHotels: Hotel[] = [];
          responses.forEach((response) => {
            const newHotels = response.data.hotels || [];
            allNewHotels = [...allNewHotels, ...newHotels];
          });

          if (allNewHotels.length === 0) {
            break;
          }

          hotelsCache = [...hotelsCache, ...allNewHotels];

          // Volver a filtrar con el cache actualizado
          filteredResults = hotelsCache.filter((hotel: Hotel) => {
            // Validar que el hotel tenga la estructura correcta
            if (!hotel.name || !hotel.name.content) return false;
            return hotel.name.content
              .toLowerCase()
              .includes(searchTerm.toLowerCase());
          });
        } catch {
          break;
        }
      }
    } else {
      filteredResults = hotelsCache;
    }

    return filteredResults.slice(0, 10);
  } catch (_error) {
    return [];
  }
};

/**
 * Búsqueda combinada: destinos y hoteles
 */
export const searchAll = async (searchTerm: string) => {
  if (!searchTerm || searchTerm.length < 2) {
    return {
      destinations: [],
      hotels: [],
    };
  }

  try {
    const [destinations, hotels] = await Promise.all([
      searchDestinations(searchTerm),
      searchHotels(searchTerm),
    ]);

    return {
      destinations,
      hotels,
    };
  } catch (_error) {
    return {
      destinations: [],
      hotels: [],
    };
  }
};

/**
 * Get Hotel Details - Obtiene información detallada de un hotel específico
 * Incluye descripción, facilidades, imágenes, ubicación, etc.
 */
export const getHotelDetails = async (hotelCode: number): Promise<any> => {
    const { signature } = await generateSignature();

    const response = await hotelbedsClient.get(`/hotels/${hotelCode}/details`, {
      headers: {
        'Api-key': HOTELBEDS_KEY,
        'X-Signature': signature,
        Accept: 'application/json',
        'Accept-Encoding': 'gzip',
      },
      params: {
        language: 'ENG',
        useSecondaryLanguage: false,
      },
    });

    const hotel = response.data.hotel || null;
    return hotel;
};

/**
 * Busca hoteles por código de destino
 * Retorna todos los hoteles del destino especificado
 */
export const searchHotelsByDestination = async (
  destinationCode: string,
): Promise<Hotel[]> => {
    const response = await hotelbedsClient.get('/hotels', {
      params: {
        fields:
          'code,name,categoryCode,description,images,address,city,postalCode,coordinates,email,web,facilities,rooms,boards,countryCode,zoneCode,ranking,accommodationType,chain,interestPoints,phones,S2C',
        language: 'ENG',
        destinationCode: destinationCode,
        from: 1,
        to: 1000,
      },
    });

    const hotels = response.data.hotels || [];
    return hotels;
};

/**
 * Busca disponibilidad y precios de hoteles
 * @param hotelCodes Array de códigos de hoteles
 * @param checkIn Fecha de check-in (formato YYYY-MM-DD)
 * @param checkOut Fecha de check-out (formato YYYY-MM-DD)
 * @param rooms Array de habitaciones con adultos y niños
 */
export interface RoomOccupancy {
  adults: number;
  children: number;
  childAges?: number[];
}

export const searchHotelAvailability = async (
  hotelCodes: number[],
  checkIn: string,
  checkOut: string,
  rooms: RoomOccupancy[],
): Promise<any> => {
    // Construir el payload según la estructura de HotelBeds Booking API
    const payload = {
      stay: {
        checkIn,
        checkOut,
      },
      occupancies: rooms.map((room) => ({
        rooms: 1,
        adults: room.adults,
        children: room.children,
        ...(room.childAges && room.childAges.length > 0
          ? {
              paxes: room.childAges.map((age) => ({
                type: 'CH',
                age,
                roomId: 1,
              })),
            }
          : {}),
      })),
      hotels: {
        hotel: hotelCodes,
      },
    };

    const response = await hotelbedsBookingClient.post('/hotels', payload);

    const hotels = response.data.hotels?.hotels || [];
    // Convertir todos los precios a USDT
    const hotelsWithUSDT = await Promise.all(
      hotels.map(async (hotel: any) => {
        if (!hotel.rooms) return hotel;

        const roomsWithUSDT = await Promise.all(
          hotel.rooms.map(async (room: any) => {
            if (!room.rates) return room;

            const ratesWithUSDT = await Promise.all(
              room.rates.map(async (rate: any) => {
                try {
                  // Usar EUR por defecto si no hay currency
                  const currency = hotel.currency || rate.currency || 'EUR';
                  const originalNet = parseFloat(rate.net);
                  const originalSellingRate = rate.sellingRate
                    ? parseFloat(rate.sellingRate)
                    : null;

                  // Convertir a USDT
                  const netUSDT = await convertToUSDT(originalNet, currency);
                  const sellingRateUSDT = originalSellingRate
                    ? await convertToUSDT(originalSellingRate, currency)
                    : null;

                  // Aplicar comisión
                  const netWithCommission = netUSDT * (1 + TRAVELER_COMMISSION);
                  const sellingRateWithCommission = sellingRateUSDT
                    ? sellingRateUSDT * (1 + TRAVELER_COMMISSION)
                    : null;

                  return {
                    ...rate,
                    originalCurrency: currency,
                    originalNet: originalNet.toString(),
                    originalSellingRate: originalSellingRate?.toString(),
                    net: netWithCommission.toFixed(2),
                    sellingRate: sellingRateWithCommission
                      ? sellingRateWithCommission.toFixed(2)
                      : rate.sellingRate,
                    currency: 'USDT',
                  };
                } catch (_error) {
                  return rate; // Retornar rate original si hay error
                }
              }),
            );

            return { ...room, rates: ratesWithUSDT };
          }),
        );

        return { ...hotel, rooms: roomsWithUSDT, currency: 'USDT' };
      }),
    );

    return hotelsWithUSDT;
};

/**
 * Check Rate - Revalúa precio y disponibilidad de un rate específico
 * Solo necesario para rates con rateType "RECHECK"
 */
export const searchCheckRate = async (rateKey: string): Promise<any> => {
    const payload = {
      rooms: [
        {
          rateKey,
        },
      ],
    };

    const response = await hotelbedsBookingClient.post('/checkrates', payload);

    const hotel = response.data.hotel || null;
    // Convertir precios a USDT
    if (hotel && hotel.rooms) {
      const roomsWithUSDT = await Promise.all(
        hotel.rooms.map(async (room: any) => {
          if (!room.rates) return room;

          const ratesWithUSDT = await Promise.all(
            room.rates.map(async (rate: any) => {
              try {
                // Usar EUR por defecto si no hay currency
                const currency = hotel.currency || rate.currency || 'EUR';
                const originalNet = parseFloat(rate.net);
                const originalSellingRate = rate.sellingRate
                  ? parseFloat(rate.sellingRate)
                  : null;

                // Convertir a USDT
                const netUSDT = await convertToUSDT(originalNet, currency);
                const sellingRateUSDT = originalSellingRate
                  ? await convertToUSDT(originalSellingRate, currency)
                  : null;

                // Aplicar comisión
                const netWithCommission = netUSDT * (1 + TRAVELER_COMMISSION);
                const sellingRateWithCommission = sellingRateUSDT
                  ? sellingRateUSDT * (1 + TRAVELER_COMMISSION)
                  : null;

                return {
                  ...rate,
                  originalCurrency: currency,
                  originalNet: originalNet.toString(),
                  originalSellingRate: originalSellingRate?.toString(),
                  net: netWithCommission.toFixed(2),
                  sellingRate: sellingRateWithCommission
                    ? sellingRateWithCommission.toFixed(2)
                    : rate.sellingRate,
                  currency: 'USDT',
                };
              } catch (_error) {
                return rate; // Retornar rate original si hay error
              }
            }),
          );

          return { ...room, rates: ratesWithUSDT };
        }),
      );

      return { ...hotel, rooms: roomsWithUSDT, currency: 'USDT' };
    }

    return hotel;
};

/**
 * Create Booking - Confirma una reserva
 */
interface BookingPax {
  roomId: number;
  type: 'AD' | 'CH'; // AD = Adult, CH = Child
  name: string;
  surname: string;
  age?: number; // Requerido para niños
}

interface BookingRoom {
  rateKey: string;
  paxes: BookingPax[];
}

interface BookingRequest {
  holder: {
    name: string;
    surname: string;
    email?: string;
    phone?: string;
  };
  rooms: BookingRoom[];
  clientReference: string;
  remark?: string;
  tolerance?: number; // Diferencia de precio permitida (default: 2.00)
}

export const createBooking = async (
  bookingData: BookingRequest,
): Promise<any> => {
    const response = await hotelbedsBookingClient.post(
      '/bookings',
      bookingData,
    );

    const booking = response.data.booking || null;
    return booking;
};

export default {
  searchDestinations,
  searchHotels,
  searchAll,
  getHotelDetails,
  searchHotelsByDestination,
  searchHotelAvailability,
  searchCheckRate,
  createBooking,
};

// ============================================
// ACTIVITIES API
// ============================================

/**
 * Interfaces para Activities
 */
export interface ActivitySearchParams {
  destination: string; // Código de destino o coordenadas
  from: string; // Fecha inicio (YYYY-MM-DD)
  to: string; // Fecha fin (YYYY-MM-DD)
  paxes: ActivityPax[];
  language?: string;
  pagination?: {
    itemsPerPage?: number;
    page?: number;
  };
}

export interface ActivityPax {
  age: number;
  type?: 'ADULT' | 'CHILD'; // Se calcula automáticamente según edad
}

export interface ActivityModality {
  code: string;
  name: string;
  amountFrom: number;
  currency: string;
  duration?: {
    value: number;
    metric: string;
  };
}

export interface Activity {
  code: string;
  name: string;
  description?: string;
  images?: Array<{ url: string }>;
  modalities: ActivityModality[];
  country?: {
    code: string;
    name: string;
  };
  content?: any;
}

/**
 * Search Activities - Busca disponibilidad de actividades
 * Endpoint: POST /activities
 */
export const searchActivities = async (
  params: ActivitySearchParams,
): Promise<any> => {
  try {
    console.log('[Activities] Searching activities...', params);

    const payload = {
      language: params.language || 'en',
      from: params.from,
      to: params.to,
      pagination: {
        itemsPerPage: params.pagination?.itemsPerPage || 20,
        page: params.pagination?.page || 1,
      },
      destination: params.destination,
      paxes: params.paxes.map((pax) => ({
        age: pax.age,
      })),
    };

    console.log('[Activities] Payload:', JSON.stringify(payload, null, 2));

    const response = await hotelbedsActivityClient.post('/activities', payload);

    const activities = response.data.activities || [];
    console.log(`[Activities] Found ${activities.length} activities`);

    return response.data;
  } catch (error: any) {
    console.error('[Activities] Error:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get Activity Detail - Obtiene detalles de una actividad específica
 * Endpoint: POST /activities/details
 */
export const getActivityDetail = async (
  activityCode: string,
  from: string,
  to: string,
  paxes: ActivityPax[],
): Promise<any> => {
  try {
    console.log(
      `[Activity Detail] Fetching details for activity ${activityCode}`,
    );

    const payload = {
      language: 'en',
      from,
      to,
      paxes: paxes.map((pax) => ({
        age: pax.age,
      })),
      activities: [
        {
          code: activityCode,
        },
      ],
    };

    console.log('[Activity Detail] Payload:', JSON.stringify(payload, null, 2));

    const response = await hotelbedsActivityClient.post(
      '/activities/details',
      payload,
    );

    const activity = response.data.activities?.[0] || null;
    console.log('[Activity Detail] Details fetched successfully');

    return activity;
  } catch (error: any) {
    console.error(
      '[Activity Detail] Error:',
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Confirm Activity Booking - Confirma una reserva de actividad
 * Endpoint: POST /bookings
 */
export interface ActivityBookingRequest {
  holder: {
    name: string;
    surname: string;
    email: string;
    phone?: string;
  };
  activities: Array<{
    rateKey: string;
    paxes: Array<{
      age: number;
      name: string;
      surname: string;
    }>;
  }>;
  clientReference: string;
  language?: string;
}

export const confirmActivityBooking = async (
  bookingData: ActivityBookingRequest,
): Promise<any> => {
  try {
    console.log('[Activity Booking] Creating booking...');
    console.log(
      '[Activity Booking] Payload:',
      JSON.stringify(bookingData, null, 2),
    );

    const payload = {
      ...bookingData,
      language: bookingData.language || 'en',
    };

    const response = await hotelbedsActivityClient.post('/bookings', payload);

    const booking = response.data.booking || null;
    console.log('[Activity Booking] Booking created successfully');
    console.log('[Activity Booking] Reference:', booking?.reference);

    return booking;
  } catch (error: any) {
    console.error(
      '[Activity Booking] Error:',
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Search Activity Destinations - Busca destinos para actividades
 * Similar a searchDestinations pero para el content API de activities
 */
export const searchActivityDestinations = async (
  searchTerm: string,
): Promise<any[]> => {
  try {
    console.log(`[Activity Destinations] Searching for: ${searchTerm}`);

    // Por ahora usar los mismos destinos que hoteles
    // En producción se puede usar el content API específico de activities
    const destinations = await searchDestinations(searchTerm);

    return destinations;
  } catch (error) {
    console.error('[Activity Destinations] Error:', error);
    return [];
  }
};

/**
 * Cancel Booking - Cancela una reserva de hotel
 * Endpoint: DELETE /bookings/{bookingId}
 */
export const cancelBooking = async (bookingId: string): Promise<any> => {
  try {
    console.log(`[Cancel Booking] Cancelling booking: ${bookingId}`);

    const response = await hotelbedsBookingClient.delete(
      `/bookings/${bookingId}`,
    );

    console.log('[Cancel Booking] Booking cancelled successfully');
    console.log('[Cancel Booking] Response:', response.data);

    return response.data;
  } catch (error: any) {
    console.error(
      '[Cancel Booking] Error:',
      error.response?.data || error.message,
    );
    throw error;
  }
};
